/**
 * Bridges the six device-local pref stores (spec 07) onto the sync engine's
 * `'settings'` entity (spec 17): one whole-document, last-write-wins row per
 * user. This is a bridge, not a refactor — the six stores keep owning
 * localStorage + their side effects (e.g. `<html data-theme>`); this module
 * only mirrors their combined state into IDB for sync.
 *
 * - Local edit → any of the six stores changes → write the combined payload
 *   to the IDB `settings` row (`updatedAt = now`, `dirty = 1`) and poke the
 *   existing 10 s sync debounce.
 * - Server pull → `applyServerSettings` (called from the syncService adapter)
 *   applies iff the incoming doc is newer (LWW), writing each field back
 *   through the stores' own setters so localStorage + side effects stay
 *   correct, then persists the applied doc as the new local baseline
 *   (`dirty = 0`). A suppress flag guards the store subscriptions above from
 *   treating that write-back as a new local edit (which would re-dirty the
 *   row and bounce it straight back to the server).
 */
import type { ServerChange } from '@boatnav/core';
import type { SettingsPayload } from '@boatnav/schemas';
import { getSettingsRecord, putSettingsRecord } from '../../lib/db.js';
import { notifyMutation } from './syncService.js';
import { useThemeStore } from '../../app/themeStore.js';
import { useTextScaleStore } from '../../app/textScaleStore.js';
import { useUnitsStore } from '../../app/unitsStore.js';
import { useTrailStore } from '../nav/trailStore.js';
import { usePredictorStore } from '../nav/predictorStore.js';
import { useDepthStore } from '../map/depthStore.js';
import { useSeamarkStore } from '../map/seamarkStore.js';

let suppress = false;
let started = false;
let unsubs: Array<() => void> = [];

function currentPayload(): SettingsPayload {
  const { mode: themeMode } = useThemeStore.getState();
  const { scale: textScale } = useTextScaleStore.getState();
  const { speed, distance, consumption, courseRef } = useUnitsStore.getState();
  const { mode: trailMode } = useTrailStore.getState();
  const { mode: predictorMode } = usePredictorStore.getState();
  const { on: depthOverlay } = useDepthStore.getState();
  const { on: seamarkOverlay } = useSeamarkStore.getState();
  return {
    themeMode,
    textScale,
    units: { speed, distance, consumption, courseRef },
    trailMode,
    predictorMode,
    depthOverlay,
    seamarkOverlay,
  };
}

/** Mirror the six stores' current state into the IDB outbox row and poke sync. */
async function persistLocalChange(): Promise<void> {
  if (suppress) return; // this change came from applyServerSettings, not a real edit
  await putSettingsRecord(currentPayload(), Date.now(), 1);
  notifyMutation();
}

/** Arm the six store subscriptions. Safe to call while anonymous — writes to
 *  IDB are harmless offline/logged-out; `notifyMutation` itself is a no-op
 *  unless logged in (spec 08). Idempotent. */
export function startSettingsSync(): void {
  if (started) return;
  started = true;

  unsubs = [
    useThemeStore.subscribe((s, prev) => {
      if (s.mode !== prev.mode) void persistLocalChange();
    }),
    useTextScaleStore.subscribe((s, prev) => {
      if (s.scale !== prev.scale) void persistLocalChange();
    }),
    useUnitsStore.subscribe((s, prev) => {
      if (
        s.speed !== prev.speed ||
        s.distance !== prev.distance ||
        s.consumption !== prev.consumption ||
        s.courseRef !== prev.courseRef
      ) {
        void persistLocalChange();
      }
    }),
    useTrailStore.subscribe((s, prev) => {
      if (s.mode !== prev.mode) void persistLocalChange();
    }),
    usePredictorStore.subscribe((s, prev) => {
      if (s.mode !== prev.mode) void persistLocalChange();
    }),
    useDepthStore.subscribe((s, prev) => {
      if (s.on !== prev.on) void persistLocalChange();
    }),
    useSeamarkStore.subscribe((s, prev) => {
      if (s.on !== prev.on) void persistLocalChange();
    }),
  ];
}

/**
 * Apply a pulled settings document (called from the syncService `SyncStore`
 * adapter). LWW guard: apply iff `change.updatedAt` is newer than the local
 * row's — a locally-newer unsynced edit is left alone and wins on the next
 * push. Settings are never deleted (spec 17): a null/tombstone payload is a
 * no-op here, not a clear.
 */
export async function applyServerSettings(change: ServerChange): Promise<void> {
  if (!change.payload) return;
  const local = await getSettingsRecord();
  if (local && local.updatedAt > change.updatedAt) return;

  const p = change.payload as Partial<SettingsPayload>;
  suppress = true;
  try {
    if (p.themeMode) useThemeStore.getState().setMode(p.themeMode);
    if (p.textScale) useTextScaleStore.getState().setScale(p.textScale);
    if (p.units) useUnitsStore.getState().set(p.units);
    if (p.trailMode) useTrailStore.getState().setMode(p.trailMode);
    if (p.predictorMode) usePredictorStore.getState().setMode(p.predictorMode);
    if (p.depthOverlay != null) useDepthStore.getState().setOn(p.depthOverlay);
    if (p.seamarkOverlay != null) useSeamarkStore.getState().setOn(p.seamarkOverlay);
  } finally {
    suppress = false;
  }
  // Re-read through the stores (normalizes any partial/garbage fields the
  // same way a local edit would) and persist as the new synced baseline.
  await putSettingsRecord(currentPayload(), change.updatedAt, 0);
}

/** Test-only escape hatch: reset module-level state (and unsubscribe the six
 *  store listeners) between test cases — production code never calls this. */
export function resetSettingsSyncForTests(): void {
  suppress = false;
  started = false;
  for (const unsub of unsubs) unsub();
  unsubs = [];
}
